import { Component, computed, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { TabPanelComponent } from '../../../../../common/tab-view/tab-panel.component';
import { TabViewComponent } from '../../../../../common/tab-view/tab-view.component';
import { ApplicantForm } from './forms/applicant.form';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApplicantAppEntityApplicantWrite,
  ApplicationAppEntityApplicationRead,
  EntityTypeAppEntityDictionaryEntityType,
  PatchApplicantApplicantItemRequestParams,
  ServiceAppEntityServiceRead
} from '../../../../../openapi';
import {
  selectApplication,
  selectEntityTypes,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  createApplication,
  loadEntityTypes,
  setApplication,
  updateApplicants
} from '../../../../../store/actions/application.actions';
import { NaturalPersonFormComponent } from './components/natural-person-form/natural-person-form.component';
import { InstitutionFormComponent } from './components/institution-form/institution-form.component';
import { CompanyFormComponent } from './components/company-form/company-form.component';
import { EntityLabelPipe } from '../../../../../pipes/applicant-label.pipe';
import { ApplicantItemPreviewComponent } from './components/applicant-item-preview/applicant-item-preview.component';
import { selectServiceByUrl } from '../../../../../store/selectors/app.selectors';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { ProxyPersonForm } from './forms/proxy-person.form';
import { ProxyFormComponent } from './components/proxy-person-form/proxy-form.component';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { CustomValidator } from "../../../../../common/forms/custom.validators";

export interface ApplicantDataComponentConfig {
  stages: string[];
  hidePeselControl?: boolean;
  requiredPeselControl?: boolean;
}

@Component({
  selector: 'app-applicant-data-section',
  standalone: true,
  imports: [
    CommonModule,
    AccordionGroupComponent,
    AccordionItemComponent,
    TabPanelComponent,
    TabViewComponent,
    SpinnerComponent,
    NaturalPersonFormComponent,
    InstitutionFormComponent,
    CompanyFormComponent,
    EntityLabelPipe,
    ApplicantItemPreviewComponent,
    StagesComponent,
    ProxyFormComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent
  ],
  templateUrl: './applicant-data-section.component.html',
  providers: [
    ApplicantForm,
    ProxyPersonForm
  ]
})
export class ApplicantDataSectionComponent implements OnDestroy, OnInit {

  public serviceType: Signal<ServiceAppEntityServiceRead | null> = this.store$.selectSignal(selectServiceByUrl(this.route.parent?.snapshot.data['contentUrl'] || ''));

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public editing = true;

  public canEdit = false;

  public applicants: WritableSignal<PatchApplicantApplicantItemRequestParams[]> = signal([]);

  public entityTypes: Signal<EntityTypeAppEntityDictionaryEntityType[]> = this.store$.selectSignal(selectEntityTypes);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private applicationApplicants: Signal<PatchApplicantApplicantItemRequestParams[]> = computed(() => {
    const app = this.application();
    if (app?.isFiledByProxy) {
      this.proxyPersonForm.patchFormValues(app);
    }
    if (app?.applicants?.length) {
      this.editing = false;
      this.canEdit = true;
    }
    return app?.applicants?.map(applicant => {
      const v: PatchApplicantApplicantItemRequestParams = {
        id: '',
        applicant: {
          application: app?.uuid,
          entityType: applicant.entityType?.id || '',
          firstName: applicant?.firstName || '',
          lastName: applicant?.lastName || '',
          name: applicant?.name || '',
          pesel: applicant?.pesel || '',
          nip: applicant?.nip || '',
          city: applicant.city,
          email: applicant.email,
          apartmentNumber: applicant.apartmentNumber,
          houseNumber: applicant.houseNumber,
          phoneNumber: applicant.phoneNumber,
          phoneNumber2: applicant.phoneNumber2,
          postalCode: applicant.postalCode,
          postOffice: applicant.postOffice,
          street: applicant.street,
          country: applicant?.country || '',
          voivodeship: applicant?.voivodeship || '',
          county: applicant?.county || '',
          municipality: applicant?.municipality || '',

          differentCorrespondenceAddress: !!applicant?.differentCorrespondenceAddress,

          correspondenceCountry: applicant?.correspondenceCountry,
          correspondenceVoivodeship: applicant?.correspondenceVoivodeship,
          correspondenceCounty: applicant?.correspondenceCounty,
          correspondenceMunicipality: applicant?.correspondenceMunicipality,
          correspondenceCity: applicant?.correspondenceCity,
          correspondencePostalCode: applicant?.correspondencePostalCode,
          correspondencePostOffice: applicant?.correspondencePostOffice,
          correspondenceStreet: applicant?.correspondenceStreet,
          correspondenceHouseNumber: applicant?.correspondenceHouseNumber,
          correspondenceApartmentNumber: applicant?.correspondenceHouseNumber
        }
      };
      return v;
    }) || [];
  });

  private fetchApplicants: Subscription = toObservable(this.applicationApplicants).subscribe(v => {
    this.applicants.set(v);
  });

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  @ViewChild('tabViewComponent', {static: false})
  tabViewComponent: TabViewComponent | undefined;

  constructor(
    public applicantForm: ApplicantForm,
    public proxyPersonForm: ProxyPersonForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onAdd(): void {
    this.editing = true;
    this.canEdit = false;
    this.currentIndex.set(undefined);
  }

  public onCancel(): void {
    this.router.navigate([this.applicationId ? '../../../' : '../../'], {relativeTo: this.route})
      .then(() => this.store$.dispatch(setApplication({application: null})));
  }

  public submitAndGoNext(): void {
    if (this.applicants().length && this.proxyPersonForm.valid) {
      if (this.applicationId) {
        this.updateApplication();
      } else {
        this.createApplication();
      }
    }
    if (this.proxyPersonForm.invalid) {
      this.proxyPersonForm.markAllAsTouched();
    }
  }

  private createApplication(): void {
    this.store$.dispatch(
      createApplication({
        application: this.proxyPersonForm.toWriteModel(),
        applicants: this.applicants(),
        serviceTypeId: this.serviceType()?.uuid || '',
        successFnc: (applicationId: string) => {
          this.router.navigate(
            ['../', applicationId, 'dane-wniosku'],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      })
    );
  }

  private updateApplication(): void {
    this.store$.dispatch(updateApplicants({
      applicationId: this.applicationId || '',
      application: this.proxyPersonForm.toWriteModel(),
      applicants: this.applicants(),
      successFnc: (applicationId) => {
        this.router.navigate(
          ['../../', applicationId, 'dane-wniosku'],
          {
            relativeTo: this.route
          }
        );
      },
      failureFnc: () => {
      }
    }));
  }

  public onUpdateApplicants(applicant: ApplicantAppEntityApplicantWrite): void {
    if (this.currentIndex() !== undefined) {
      this.applicants.update(v => {
        v[this.currentIndex() || 0] = {applicant, id: v[this.currentIndex() || 0].id};
        return v;
      });
      this.currentIndex.set(undefined);
    } else {
      this.applicants.update(v => ([...v, {applicant, id: ''}]));
    }
    this.editing = false;
    this.canEdit = true;
    this.applicantForm.reset();
  }

  public onRemove(i: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.applicants.update(v => {
      v.splice(i, 1);
      return v;
    });
  }

  public onEditApplicant(event: Event, i: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentIndex.set(i);
    this.editing = true;
    this.canEdit = false;

    // todo
    setTimeout(() => {

      const v = this.applicants()[i].applicant;
      const tabName: string = this.entityTypes().filter(type => type.id === v?.entityType)[0].name || '';
      this.tabViewComponent?.onNavigate(this.tabViewComponent?.panels.filter(v => v?.name === tabName)[0]);

      if (v) {
        this.applicantForm.patchFormValues(v);
      }
    });
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadEntityTypes());
    if (this.route.snapshot.data['hidePeselControl']) {
      this.applicantForm.removeControl('pesel');
    }
    if (this.route.snapshot.data['requiredPeselControl']) {
      this.applicantForm.pesel?.setValidators([Validators.required, CustomValidator.pesel()]);
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplicants.unsubscribe();
  }
}
